// libraries
import React, { useEffect, useState, useRef } from 'react'

// components
import Section from '/react4xp/components/Section'
import { UxSpinner } from '/react4xp/components/Svg/Ux'

// utils
import { isNonWhitespaceString, useEqualizeHeights } from '/lib/reactutils/utils'

// props
export interface MemberSignUpItem {
	title?: string
	icon?: string
	price?: string
	link?: string
}

export interface MemberSignUpProps {
	heading?: string
	background?: 'white' | 'light' | 'dark'
	items?: MemberSignUpItem[]
	initialModalOpen?: boolean // For content generator - show modal opened by default
}

const mapSectionBackground = (background: MemberSignUpProps['background']) => {
	switch (background) {
		case 'light':
			return 'secondary'
		case 'dark':
			return 'primary'
		case 'white':
		default:
			return 'white'
	}
}

export default function MemberSignUp({
	heading,
	background = 'white',
	items = [],
	initialModalOpen = false
}: MemberSignUpProps) {
	const [isModalOpen, setIsModalOpen] = useState(initialModalOpen)
	const [selectedItem, setSelectedItem] = useState<MemberSignUpItem | null>(null)
	const [isIframeLoading, setIsIframeLoading] = useState(false)
	const itemsContainerRef = useRef<HTMLDivElement>(null)

	const safeItems = Array.isArray(items) ? items : []
	const validItems = safeItems.filter((item) => {
		const hasTitle = isNonWhitespaceString(item?.title)
		const hasLink = isNonWhitespaceString(item?.link)
		return hasTitle && hasLink
	})

	// Equalize heights of titles and prices (icons stay fixed at 120px)
	useEqualizeHeights(itemsContainerRef, [
		'.member-signup__item-title',
		'.member-signup__item-price'
	])

	const openItem = (item: MemberSignUpItem) => {
		if (item?.link && isNonWhitespaceString(item.link)) {
			setSelectedItem(item)
			setIsModalOpen(true)
			setIsIframeLoading(true)
		}
	}

	const handleCloseModal = () => {
		setIsModalOpen(false)
		setSelectedItem(null)
		setIsIframeLoading(false)
	}

	useEffect(() => {
		if (initialModalOpen) {
			const firstWithLink = validItems.find((item) => item.link && isNonWhitespaceString(item.link))
			if (firstWithLink) {
				setSelectedItem(firstWithLink)
				setIsModalOpen(true)
				setIsIframeLoading(true)
			}
		}
	}, [initialModalOpen, items])

	const modalTitle = selectedItem?.title && isNonWhitespaceString(selectedItem.title) ? selectedItem.title : 'Velg medlemskapstype'

	return (
		<>
			<Section
				bgColor={mapSectionBackground(background)}
				className={`member-signup member-signup--${background}`}
			>
				<div className='container'>
				<div className='member-signup__wrapper'>

					{heading && isNonWhitespaceString(heading) && (
						<h3 className='member-signup__heading'>{heading}</h3>
					)}

					<div className='member-signup__items' ref={itemsContainerRef}>
							{validItems.map((item, index) => {
								const iconPath = isNonWhitespaceString(item.icon) ? item.icon as string : undefined
								const isSvg = iconPath ? iconPath.toLowerCase().endsWith('.svg') : false
								const isInteractive = item.link && isNonWhitespaceString(item.link)

								return (
									<div key={index} className='member-signup__item'>
										<button
											className={`member-signup__item-content${isInteractive ? '' : ' member-signup__item-content--disabled'}`}
											onClick={() => isInteractive && openItem(item)}
											disabled={!isInteractive}
											onKeyDown={(e) => {
												if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
													e.preventDefault()
													openItem(item)
												}
											}}
										>
											{item.title && isNonWhitespaceString(item.title) && (
												<span
													className='member-signup__item-title'
													dangerouslySetInnerHTML={{ __html: item.title }}
													suppressHydrationWarning
												/>
											)}
											<span className='member-signup__item-content-inner'>
												{iconPath && isNonWhitespaceString(iconPath) && (
													<span className={`member-signup__item-icon ${isSvg ? 'member-signup__item-icon--svg' : ''}`}>
														<img src={iconPath} alt='' />
													</span>
												)}
											{item.price && isNonWhitespaceString(item.price) && (
												<span className='member-signup__item-price'>{item.price}</span>
											)}
											</span>
										</button>
									</div>
								)
							})}
						</div>
					</div>
				</div>
			</Section>

			{isModalOpen && selectedItem?.link && (
				<div className='member-signup__modal' aria-modal='true' aria-label={modalTitle} onClick={handleCloseModal}>
					<div className={`member-signup__modal-content member-signup__modal-content--${background}`} onClick={(e) => e.stopPropagation()}>
						<button
							type='button'
							className='member-signup__modal-close'
							onClick={handleCloseModal}
							aria-label='Lukk'
						>
							×
						</button>

						<div className='member-signup__modal-iframe-wrapper'>
							{isIframeLoading && (
								<div className='member-signup__modal-loading'>
									<UxSpinner />
								</div>
							)}
							<iframe
								src={selectedItem.link}
								title='Medlemsregistrering'
								className='member-signup__modal-iframe'
								onLoad={() => setIsIframeLoading(false)}
							/>
						</div>
					</div>
				</div>
			)}
		</>
	)
}
